import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import EmptyState from '@/components/feature/EmptyState';
import { categories, durationOptions } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import type { Video } from '@/types/video';
import { filterVideos } from '@/utils/filter';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const allVideos = videos as Video[];

  const [input, setInput] = useState(keyword);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [durationFilter, setDurationFilter] = useState('all');

  useDocumentTitle({
    title: keyword ? `搜索「${keyword}」- 成人快手` : '搜索 - 成人快手',
    description: keyword
      ? `成人快手搜索「${keyword}」的结果，包含相关成人主题短视频内容。`
      : '在成人快手搜索你感兴趣的成人主题短视频内容。',
    keywords: '成人快手,搜索,短视频,成人视频',
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    setCategoryFilter('all');
    setDurationFilter('all');
  }, [keyword]);

  // Live search with debounce
  useEffect(() => {
    if (input === keyword) return;
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (input.trim()) next.set('q', input.trim());
      else next.delete('q');
      setSearchParams(next, { replace: true });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const results = useMemo(
    () =>
      filterVideos(allVideos, {
        keyword,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        duration: durationFilter,
        sort: 'hot',
      }),
    [allVideos, keyword, categoryFilter, durationFilter],
  );

  const recommended = useMemo(
    () => [...allVideos].sort((a, b) => b.views - a.views).slice(0, 4),
    [allVideos],
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set('q', input.trim());
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-10">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

      <div className="mt-5">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {keyword ? `「${keyword}」的搜索结果` : '搜索视频'}
        </h1>
        <p className="mt-1 text-sm text-foreground-500">
          {keyword ? `共找到 ${results.length} 个相关视频` : '输入关键词，搜索视频标题、标签或作者'}
        </p>
      </div>

      <form
        onSubmit={submit}
        className="mt-6 flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-3"
      >
        <i className="ri-search-line text-lg text-foreground-400" />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="搜索视频、标签、作者"
          className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
        />
        {input && (
          <button
            type="button"
            onClick={() => setInput('')}
            className="flex h-7 w-7 items-center justify-center rounded-full text-foreground-400 hover:text-foreground-800"
            aria-label="清空"
          >
            <i className="ri-close-line text-lg" />
          </button>
        )}
        <button
          type="submit"
          className="hidden shrink-0 rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-foreground-950 transition-colors hover:bg-primary-400 sm:block"
        >
          搜索
        </button>
      </form>

      {/* Filters */}
      <div className="mt-4 flex flex-col gap-3 rounded-lg border border-background-200/70 bg-background-100 p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="w-20 shrink-0 text-xs font-medium text-foreground-500">分类</span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCategoryFilter('all')}
              className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                categoryFilter === 'all' ? 'bg-primary-500 font-medium text-foreground-950' : 'bg-background-200/70 text-foreground-700'
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategoryFilter(c.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                  categoryFilter === c.id ? 'bg-primary-500 font-medium text-foreground-950' : 'bg-background-200/70 text-foreground-700'
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <span className="w-20 shrink-0 text-xs font-medium text-foreground-500">时长</span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setDurationFilter('all')}
              className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                durationFilter === 'all' ? 'bg-primary-500 font-medium text-foreground-950' : 'bg-background-200/70 text-foreground-700'
              }`}
            >
              全部
            </button>
            {durationOptions.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setDurationFilter(d.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${
                  durationFilter === d.id ? 'bg-primary-500 font-medium text-foreground-950' : 'bg-background-200/70 text-foreground-700'
                }`}
              >
                {d.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        {keyword && results.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {results.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        )}

        {keyword && results.length === 0 && (
          <EmptyState
            icon="ri-search-eye-line"
            title="没有找到相关视频"
            description="换个关键词试试，或者看看下面为你推荐的热门内容。"
            actionLabel="返回首页"
            actionTo="/"
          />
        )}

        {!keyword && (
          <EmptyState
            icon="ri-search-line"
            title="开始搜索吧"
            description="在上方输入关键词，搜索你感兴趣的成人主题短视频。"
          />
        )}
      </div>

      {keyword && results.length === 0 && (
        <section className="mt-12">
          <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">为你推荐</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {recommended.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}